import { useEffect, useState } from "react";
import { GraduationCap, ExternalLink, HandCoins, Sparkles, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * ScholarshipsCard — deep-linked list of every scholarship a candidate
 * could apply for, sourced from
 * `/api/universities/{id}/scholarships`. Normalised across the scraped
 * schema (`value_gbp / eligibility / deadline / source_url`) and the
 * older editorial seed (`avg_amount_usd / criteria`).
 *
 * Sorted by amount, then confidence. Confidence chips: HIGH · MEDIUM
 * · EDITORIAL · LOW.
 *
 * Hides gracefully when the endpoint returns an empty list.
 */
export default function ScholarshipsCard({ uniId, format }) {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/scholarships`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  if (!data) return null;
  const allRows = data.scholarships || [];
  if (allRows.length === 0) return null;

  // Filter: keep rows whose eligibility text mentions the subject OR
  // rows that don't reference a subject at all (open to everyone). If
  // subject is empty, show all rows.
  const s = subject.trim().toLowerCase();
  const rows = !s ? allRows : allRows.filter((r) => {
    const elig = (r.eligibility || "").toLowerCase();
    // Show rows without any subject constraint (open awards) plus
    // rows whose eligibility text explicitly matches the subject.
    const hasSubjectMarker = /(medic|law|engineer|business|scienc|art|music|econom|comput|classic|history|philosoph|theolog|maths|mathematic|physic|chemistr|biolog|literatur|linguist|architect|film|drama)/i.test(elig);
    return !hasSubjectMarker || elig.includes(s);
  });

  const fmt = typeof format === "function"
    ? format
    : (v) => (v ? `$${v.toLocaleString()}` : "—");

  return (
    <section className="mb-8" data-testid="scholarships-card">
      <div>
        <TabKicker>03 · Scholarships & Bursaries</TabKicker>
        <TabSectionTitle>Every award you could apply for</TabSectionTitle>
      </div>

      {/* Course-specific filter — free-text keyword input. Filters
          out awards whose eligibility text names a *different*
          subject, keeps subject-agnostic awards in every filter. */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">
          Filter by course
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Medicine, Law, Engineering"
          className="border border-[#002147]/20 bg-white px-3 py-1.5 font-mono text-sm text-[#002147] focus:outline-none focus:border-[#7C3AED] w-64"
          data-testid="scholarships-subject-filter"
        />
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
          {rows.length}/{allRows.length} awards
        </span>
      </div>

      <div className="border border-[#002147]/10 bg-white/40 overflow-hidden">
        <ul className="divide-y divide-[#002147]/10">
          {rows.slice(0, 12).map((s, i) => (
            <li
              key={i}
              className="p-5 hover:bg-[#F3F1EC]/50 transition-colors"
              data-testid={`scholarship-row-${i}`}
            >
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex items-start gap-3 min-w-0">
                  <HandCoins className="h-4 w-4 mt-1 shrink-0 text-[#7C3AED]" />
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#002147] leading-tight">
                      {s.name}
                    </div>
                    {s.eligibility && (
                      <div className="mt-1 font-sans text-xs text-[#002147]/70 leading-relaxed">
                        {s.eligibility}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif text-xl text-[#002147] leading-none">
                    {s.amount_usd ? fmt(s.amount_usd) : (s.amount_pct_tuition ? `${Math.round(s.amount_pct_tuition * 100)}% tuition` : "—")}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50 mt-1">
                    per year
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 pl-7 font-mono text-[10px] uppercase tracking-wider">
                {s.intl_eligible === true && (
                  <span className="inline-flex items-center gap-1 border border-emerald-700/40 text-emerald-800 px-1.5 py-0.5">
                    <ShieldCheck className="h-2.5 w-2.5" /> Intl. eligible
                  </span>
                )}
                {s.deadline && (
                  <span className="border border-[#B45309]/40 text-[#B45309] px-1.5 py-0.5">
                    Deadline · {s.deadline}
                  </span>
                )}
                {s.duration_years > 1 && (
                  <span className="border border-[#002147]/20 text-[#002147]/70 px-1.5 py-0.5">
                    {s.duration_years} yrs {s.renewable ? "· renewable" : ""}
                  </span>
                )}
                <ConfidenceChip level={s.confidence} />
                {s.source_url ? (
                  <a
                    href={s.source_url}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-[#7C3AED] hover:underline"
                    data-testid={`scholarship-apply-${i}`}
                  >
                    Apply <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ) : data.scholarships_url ? (
                  <a
                    href={data.scholarships_url}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-[#7C3AED] hover:underline"
                    data-testid={`scholarship-uni-page-${i}`}
                  >
                    Uni page <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
        <Sparkles className="inline h-3 w-3 mr-1" />
        Sorted by annual value · click any row for eligibility · sources on hover
      </p>
    </section>
  );
}

function ConfidenceChip({ level }) {
  if (!level) return null;
  const style =
    level === "high" ? "border-emerald-700/40 text-emerald-800" :
    level === "medium" ? "border-[#002147]/30 text-[#002147]/70" :
    level === "editorial" ? "border-[#7C3AED]/30 text-[#7C3AED]" :
    "border-amber-700/40 text-amber-800";
  return (
    <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 ${style}`}>
      <GraduationCap className="h-2.5 w-2.5" /> {level}
    </span>
  );
}
